import { useState, useEffect } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Save, History } from 'lucide-react';
import { toast } from 'sonner';

interface WorkflowVersion {
  id: string;
  workflowId: string;
  version: number;
  data: any;
  createdAt: Date;
  description: string;
}

export function WorkflowVersioning() {
  const { currentWorkflow, saveWorkflow } = useWorkflowStore();
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<WorkflowVersion | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [description, setDescription] = useState('');

  // Load versions from localStorage
  useEffect(() => {
    if (currentWorkflow) {
      const stored = localStorage.getItem(`workflow-versions-${currentWorkflow.id}`);
      if (stored) {
        try {
          setVersions(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to load versions:', e);
        }
      }
    }
  }, [currentWorkflow?.id]);

  const handleSaveVersion = () => {
    if (!currentWorkflow) {
      toast.error('No workflow selected');
      return;
    }

    if (!description.trim() && versions.length === 0) {
      setShowDescription(true);
      return;
    }

    const newVersion: WorkflowVersion = {
      id: Math.random().toString(36).substr(2, 9),
      workflowId: currentWorkflow.id,
      version: versions.length + 1,
      data: JSON.parse(JSON.stringify(currentWorkflow)),
      createdAt: new Date(),
      description: description || `Version ${versions.length + 1}`,
    };

    const updated = [...versions, newVersion];
    setVersions(updated);
    localStorage.setItem(`workflow-versions-${currentWorkflow.id}`, JSON.stringify(updated));
    setDescription('');
    setShowDescription(false);
    toast.success('Version saved');
  };

  const handleRestoreVersion = (version: WorkflowVersion) => {
    if (!currentWorkflow) return;

    if (confirm(`Restore to version ${version.version}?`)) {
      useWorkflowStore.setState({
        currentWorkflow: {
          ...currentWorkflow,
          nodes: version.data.nodes,
          edges: version.data.edges,
        },
      });
      saveWorkflow();
      setShowHistory(false);
      toast.success(`Restored to version ${version.version}`);
    }
  };

  const handleDeleteVersion = (versionId: string) => {
    const updated = versions.filter((v) => v.id !== versionId);
    setVersions(updated);
    if (currentWorkflow) {
      localStorage.setItem(`workflow-versions-${currentWorkflow.id}`, JSON.stringify(updated));
    }
    toast.success('Version deleted');
  };

  if (!currentWorkflow) {
    return null;
  }

  return (
    <>
      {/* Version Controls */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDescription(true)}
          title="Save current version"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Version
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowHistory(true)}
          disabled={versions.length === 0}
        >
          <History className="w-4 h-4 mr-2" />
          History ({versions.length})
        </Button>
      </div>

      {/* Save Version Dialog */}
      <Dialog open={showDescription} onOpenChange={setShowDescription}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Workflow Version</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <textarea
                placeholder="What changed in this version?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-24 p-2 border rounded text-sm resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDescription(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveVersion}>Save Version</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {versions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No versions saved yet</p>
            ) : (
              versions.map((version) => (
                <Card key={version.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">
                        Version {version.version}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {version.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(version.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestoreVersion(version)}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
